import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { Header } from "@/components/header";
import { Footer } from '@/components/footer';
import { CTA } from '@/components/cta';
import { FAQ } from '@/components/faq';

export default function AccessibilityBenefit() {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />
      <div className="container mx-auto px-4 py-16">        
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-slate-100 dark:bg-slate-900/30 w-16 h-16 rounded-2xl flex items-center justify-center text-slate-600 dark:text-slate-400">
              <Globe className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {t('benefitsCloud.benefits.1.title')}
            </h1>
          </div>
          
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-xl text-slate-600 dark:text-slate-300 mb-8">
              {t('benefitsCloud.benefits.1.description')}
            </p>
            
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                {t('benefitsCloud.details.accessibility.title', 'Access Your Security Anywhere, Anytime')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                {t('benefitsCloud.details.accessibility.description', 'Our cloud-based security solution provides instant access to your security system from any device with an internet connection, giving you peace of mind whether you\'re in the office or on the go.')}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.accessibility.benefit1', 'Mobile Access')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.accessibility.benefit1Desc', 'Monitor your premises from your smartphone or tablet with our intuitive mobile app.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.accessibility.benefit2', 'Real-time Alerts')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.accessibility.benefit2Desc', 'Get instant notifications for any security events, no matter where you are.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.accessibility.benefit3', 'Multi-User Access')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.accessibility.benefit3Desc', 'Grant secure access to team members with customizable permission levels.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.accessibility.benefit4', 'Cross-Platform Support')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.accessibility.benefit4Desc', 'Works seamlessly across all devices and operating systems.')}
                  </p>
                </div>
              </div>
              
              <div className="mt-16">
                <FAQ 
                    faqs={t('benefitsCloud.details.accessibility.faq', { returnObjects: true }) as Array<{id?: string, question: string, answer: string}>} 
                    title={t('faq.title', 'Frequently Asked Questions')}
                    subtitle={t('benefitsCloud.details.accessibility.faqSubtitle', 'Everything you need to know about accessibility')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <CTA />
      <Footer />
    </div>
  );
}
